/**
 * SelectedListsPanel — the lists this Combine run is made of, shown on their
 * own so a selected list does not "disappear" (Jose, Trello `fDtPEEJP` §3).
 *
 * The picker's own highlight is the only other place a selection shows, and it
 * is relative to the picker's page: 8 rows per page, sorted by `created_at
 * desc`, narrowed by search and owner. Another person's new upload pushes a
 * selected list off page 1, and a search hides it. This panel reads the
 * `ImportSummary` rows `CombineFilterPage` kept when each list was ticked, so
 * it depends on none of that.
 *
 * Per entry: title, contacts count, creator, created date and a remove control.
 * With "Remove contacts already in Thrive" on, also what is left of the list
 * after that removal (epic 2026-10-01-combine-stability-perf, sub-task 02):
 * `CombineSourceState.people - people_in_thrive`, shown only once the list
 * is checked against the target (before, its own rows remove nobody, so the
 * number would still grow), and only from a preview for the current target
 * (the page passes `states` filtered so).
 *
 * Presentational and controlled: no state of its own.
 */
import type { CombineSourceState, ImportSummary } from "../../../clients/imports.client";
import { formatContactCount } from "../../../features/imports/selection/importLabels";
import { displayTitle } from "../import-detail/importTitle";

export interface SelectedListsPanelProps {
  sources: readonly ImportSummary[];
  onRemove: (item: ImportSummary) => void;
  /** The lists' states from a preview for the current target and mode;
   *  `undefined` while the Thrive removal is off (nothing to show). */
  states?: readonly CombineSourceState[];
}

/** "1,234 left after removing 56 already in Thrive" / "Not checked in Thrive yet". */
function afterThriveNote(state: CombineSourceState): string {
  if (!state.checked) return "Not checked in Thrive yet";
  const people = state.people ?? 0;
  const inThrive = state.people_in_thrive ?? 0;
  return `${Math.max(0, people - inThrive).toLocaleString()} left after removing ${inThrive.toLocaleString()} already in Thrive`;
}

function formatCreated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function SelectedListsPanel({ sources, onRemove, states }: SelectedListsPanelProps) {
  if (sources.length === 0) return null;
  const stateById = new Map((states ?? []).map((s) => [s.import_id, s]));
  return (
    <section
      data-testid="combine-selected-lists"
      aria-label="Selected lists"
      className="mt-3 flex flex-col gap-1 rounded-md border border-border bg-bg p-2"
    >
      <h3 className="text-label font-semibold text-text">Selected lists ({sources.length})</h3>
      <ul className="flex flex-col divide-y divide-border">
        {sources.map((s) => {
          const title = displayTitle(s.filename);
          const state = stateById.get(s.import_id);
          return (
            <li
              key={s.import_id}
              data-testid="combine-selected-list"
              data-import-id={s.import_id}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5"
            >
              <span
                data-testid="combine-selected-list-title"
                className="min-w-40 flex-1 text-body font-medium text-text [overflow-wrap:anywhere]"
              >
                {title}
              </span>
              <span
                data-testid="combine-selected-list-count"
                className="text-caption text-text-secondary"
              >
                {formatContactCount(s.total_contacts)}
              </span>
              {state ? (
                <span
                  data-testid="combine-selected-list-after-thrive"
                  className="text-caption text-text-secondary"
                >
                  {afterThriveNote(state)}
                </span>
              ) : null}
              <span
                data-testid="combine-selected-list-creator"
                className="text-caption text-text-secondary"
              >
                {s.owner_name ?? s.uploader_email}
              </span>
              <span
                data-testid="combine-selected-list-date"
                className="text-caption text-text-secondary"
              >
                {formatCreated(s.created_at)}
              </span>
              <button
                type="button"
                data-testid="combine-selected-list-remove"
                aria-label={`Remove ${title} from the combine`}
                onClick={() => onRemove(s)}
                className="rounded-sm px-1.5 py-0.5 text-caption text-text-secondary hover:text-text hover:underline focus:outline-none focus:ring-1 focus:ring-accent"
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
